import { NgClass, NgFor } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDrawer } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { FuseDrawerComponent } from '@fuse/components/drawer';
import { FuseConfig, FuseConfigService, Scheme, Theme, Themes } from '@fuse/services/config';
import { UserServiceProxy } from '@shared/service-proxies/service-proxies';
import { ThemesService } from '@shared/services/themes.service';
import { finalize, Subject, takeUntil } from 'rxjs';

@Component({
    selector     : 'settings',
    templateUrl  : './settings.component.html',
    styles       : [
        `
            settings {
                position: static;
                display: block;
                flex: none;
                width: auto;
            }

            @media (screen and min-width: 1280px) {

                empty-layout + settings .settings-cog {
                    right: 0 !important;
                }
            }
        `,
    ],
    encapsulation: ViewEncapsulation.None,
    standalone   : true,
    imports      : [MatIconModule, FuseDrawerComponent, MatButtonModule, NgFor, NgClass, MatTooltipModule],
})
export class SettingsComponent implements OnInit, OnDestroy
{
    @ViewChild('settingsDrawer') public settingsDrawer: MatDrawer;
    config: FuseConfig;
    layout: string;
    scheme: 'dark' | 'light';
    theme: string;
    themes: Themes;
    isChangeThemeUserTriggered = false;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _router: Router,
        private _fuseConfigService: FuseConfigService,
        private _themesService: ThemesService,
        private _userService: UserServiceProxy
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to config changes
        this._fuseConfigService.config$
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    console.log('DONE updating theme settings');
                })
            )
            .subscribe((config: FuseConfig) =>
            {
                console.log('@@@ Theme Config', config);
                // Store the config
                this.config = config;


                
                if (this.isChangeThemeUserTriggered) {
                    const data = JSON.stringify(config);
                    console.log('@@@ Theme Config', data);
                    this._userService.updateUserThemeSettings(data).subscribe(() =>{});
                }

                this.isChangeThemeUserTriggered = false;
                
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    ngAfterViewInit() {
        this._themesService.toggleDrawer$
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          this.settingsDrawer.toggle();
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set the layout on the config
     *
     * @param layout
     */
    setLayout(layout: string): void
    {
        console.log('@@@ Layout', layout);
        // Clear the 'layout' query param to allow layout changes
        this._router.navigate([], {
            queryParams        : {
                layout: null,
            },
            queryParamsHandling: 'merge',
        }).then(() =>
        {
            this.isChangeThemeUserTriggered = true;

            // Set the config
            this._fuseConfigService.config = {layout};
        });
    }

    /**
     * Set the scheme on the config
     *
     * @param scheme
     */
    setScheme(scheme: Scheme): void
    {
        console.log('@@@ Scheme', scheme);
        this.isChangeThemeUserTriggered = true;

        this._fuseConfigService.config = {scheme};
    }

    /**
     * Set the theme on the config
     *
     * @param theme
     */
    setTheme(theme: Theme): void
    {
        console.log('@@@ Theme', theme);
        this.isChangeThemeUserTriggered = true;

        this._fuseConfigService.config = {theme};
    }

    toggleDrawer(): void {
        this.settingsDrawer.toggle();
    }
}
